import { NamePath, StoreValue } from 'antd/lib/form/interface';
import { HKID_REGEX } from 'src/constants/validation';
import { DEFAULT_INPUT_MAX_NUMBER, FILE_TYPES } from 'src/constants';
import { createLoadedImage } from './window';

export function isHkIdValid(
	form: { getFieldValue: (name: NamePath) => StoreValue },
	firstPartName: NamePath,
	idPartName: NamePath,
	checkDigitName: NamePath
): boolean {
	const firstPart = form.getFieldValue(firstPartName);
	const idPart = form.getFieldValue(idPartName);
	const checkDigit = form.getFieldValue(checkDigitName);
	if (!firstPart && !idPart && !checkDigit) {
		return true;
	}

	return HKID_REGEX.FULL_PART.test(`${firstPart}${idPart}${checkDigit}`);
}

export function normalizeHkIdFirstPart(
	value: string | undefined,
	currentValue: string | undefined
): string | undefined {
	if (!value) {
		return '';
	}
	const upperValue = value.toUpperCase();
	if (!HKID_REGEX.FIRST_PART.test(upperValue)) {
		return currentValue || '';
	}
	return upperValue;
}

export function normalizeHkIdPart(value: string | undefined, currentValue: string | undefined): string | undefined {
	if (!value) {
		return '';
	}
	if (value.length > 6 || Number.isNaN(value)) {
		return currentValue || '';
	}
	return value;
}

export function normalizeHkCheckDigitPart(
	value: string | undefined,
	currentValue: string | undefined
): string | undefined {
	if (!value) {
		return '';
	}
	if (!HKID_REGEX.CHECK_DIGIT.test(value)) {
		return currentValue || '';
	}
	return value;
}
function renameFile(fileName: string): string {
	const fileArr = fileName.split('.');
	return `${fileArr[0]}.jpg`;
}
// export async function fromHEICToJpg(file: File): Promise<File> {
// 	if (FILE_TYPES.HEIC !== file.type) {
// 		return file;
// 	}
// 	const rs = await heic2any({
// 		blob: file,
// 		toType: FILE_TYPES.JPG
// 	});
// 	let fileBlob: Blob;
// 	if (Array.isArray(rs)) {
// 		fileBlob = rs[0];
// 	} else {
// 		fileBlob = rs;
// 	}
// 	return new File([fileBlob], renameFile(file.name), {
// 		type: FILE_TYPES.JPG
// 	});
// }
function canvasToBlob(canvas: HTMLCanvasElement, type = FILE_TYPES.JPG): Promise<Blob | null> {
	return new Promise((rs) => {
		canvas.toBlob((result) => {
			rs(result);
		}, type);
	});
}

export async function convertPngToJpg(file: File): Promise<File> {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return file;
	}
	const imageSrc = URL.createObjectURL(file);
	const imgFile = await createLoadedImage(imageSrc);
	canvas.width = imgFile.width;
	canvas.height = imgFile.height;
	ctx.drawImage(imgFile, 0, 0);
	const fileBlob = await canvasToBlob(canvas, FILE_TYPES.JPG);

	canvas.remove();
	if (!fileBlob) {
		return file;
	}
	return new File([fileBlob], renameFile(file.name), {
		type: FILE_TYPES.JPG
	});
}

export function normalizeInputBlockNumberAndAllowChineseCharacter(value: string) {
	return value.replace(/[^A-Za-z\u4e00-\u9fa5 ]/, '');
}

export function normalizeInputBlockNumber(value: string) {
	return value.replace(/[^A-Za-z ]/, '');
}

export function normalizeInputBlockCharacter(value: string) {
	return value.replace(/[^0-9]/, '');
}

export function normalizeInputBlockNumberOnly(value: string) {
	return value.replace(/[0-9]/, '');
}

export function normalizeInputLowercaseCharacter(value: string) {
	return value.toLocaleLowerCase();
}

export function normalizeInputBlockSpecialCharacter(value: string) {
	return value.replace(/[^A-Za-z0-9]/, '');
}

export function normalizeStrNumber(value: string): number | undefined {
	if (!value || value.trim().length < 1) {
		return;
	}
	value = value.replaceAll(',', '');
	return Number(value);
}

export function convertStringToNumberForObject<T>(input: T, fields: Array<keyof T>): T {
	let rs: { [key: string]: number } = {};
	fields.forEach((k) => {
		if (input[k]) {
			let inputValue: string = input[k] as string;
			inputValue = inputValue.replaceAll(',', '');
			rs[k as string] = Number(inputValue);
		}
	});
	rs = {
		...input,
		...rs
	};
	return rs as T;
}

export function convertNumberToStringForObject<T>(input: T, fields: Array<keyof T>): T {
	let rs: { [key: string]: string } = {};
	fields.forEach((k) => {
		if (input[k]) {
			const inputValue = Number(input[k]);

			rs[k as string] = inputValue.toLocaleString('en-US');
		}
	});
	rs = {
		...input,
		...rs
	};
	return rs as T;
}

export function normalizeNumberInput(current: string, previous: string): string {
	if (!current) {
		return '';
	}
	current = current.replaceAll(',', '');
	const newValue = Number(current);

	if (isNaN(newValue) || newValue > DEFAULT_INPUT_MAX_NUMBER) {
		return previous || '';
	}
	return newValue.toLocaleString('en-US');
}

export function normalizeNumberToString(current: number): string {
	return current.toLocaleString('en-US');
}
export function normalizeToStringNumber(current: string): number {
	if (!current) {
		return 0;
	}
	current = `${current}`.replaceAll(',', '');
	const newValue = Number(current);
	return newValue;
}

export function getOrdinal(n: number) {
	let ord = 'th';

	if (n % 10 == 1 && n % 100 != 11) {
		ord = 'st';
	} else if (n % 10 == 2 && n % 100 != 12) {
		ord = 'nd';
	} else if (n % 10 == 3 && n % 100 != 13) {
		ord = 'rd';
	}
	return ord;
}

export function toOrdinal(n: number) {
	return `${n}<sup>${getOrdinal(n)}</sup>`
}

export function formatNumber(currency: string, n: string | number | undefined): string {
	if (!n) {
		return '';
	}
	const val = Number(n);
	if (isNaN(val)) {
		return '';
	}
	return `${currency} ${val.toLocaleString('en-us')}`;
}

export function formatNumberOnly(n: string | number | undefined, defaultEmpty?: string): string {
	if (!n) {
		return '';
	}
	const val = Number(n);
	if (isNaN(val)) {
		return defaultEmpty || '';
	}
	return `${val.toLocaleString('en-us')}`;
}
export function formatNumberInput(n: string | number | undefined): string {
	return n ? Number(n).toLocaleString('en-us') : '';
}
